import { SHAPE_STYLES, TEXT_STYLE } from './constants';
import { COLORS } from './constants-colors';
import {
  addGameObject,
  anchoredPos,
  getZeroStaticEntity,
  setEntityPosition,
  setParent,
  getEntityPositionXY,
} from './entity';
import { getImageUriBySize, hexToColor, multiplyColorAlpha255, v2 } from './helpers';
import { ServerEvent_EntityProps, ServerEvent_ImageObject } from './shared/api_shared_types';
import { setVisibilityState, setParentByEntityProps } from './simulate';
import {
  Entity,
  VisibilityState,
  GameManager,
  GameObjectController,
  ImageObject,
  MoveMode,
  RenderItemType,
  Shape,
  SharedObjectType,
  type V2,
} from './types';
import { createAndAddLocalTextObject } from './textobject/textobject';
import { createImageEnt } from './image_entity';
import * as DataController from './controllers/data_controller';
import { API_HOST } from './index';
import logger from './controllers/logger';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageItem,
  type RenderPackageRect,
} from './render_package';
import { layoutTextObject } from './controllers/text_layout_controller';

export function controller(): GameObjectController {
  return {
    getNew: getZeroImageObject,
    sync,
    update,
    createPackage,
  };
}

export function getZeroImageObject(ent: Entity): ImageObject {
  let newImageObject: ImageObject = {
    ...ent,
    objectType: SharedObjectType.IMAGE_OBJECT,

    label: '',
    imageUri: '',

    maxImageWidthEm: 0,
    minImageWidthEm: 0,
    maxImageHeightEm: 0,
    fixedSize: false,

    imageEnt: null,
    previousImageEnt: null,
    textObject: null,

    requestImageUpload: false,
    isWaitingForResponse: false,
    uploadedImageId: '',
    uploadedImageUrl: '',

    backgroundColor: COLORS.TRANSPARENT,
  };

  return newImageObject;
}

export function sync(
  manager: GameManager,
  imageObject: ImageObject,
  data: ServerEvent_ImageObject,
) {
  let entityProps: ServerEvent_EntityProps = {};

  if (data.hasOwnProperty('entityProps')) {
    entityProps = data.entityProps;
  }

  if (data.hasOwnProperty('maxImageWidthEm')) {
    imageObject.maxImageWidthEm = data.maxImageWidthEm;
  }

  if (data.hasOwnProperty('maxImageHeightEm')) {
    imageObject.maxImageHeightEm = data.maxImageHeightEm;
  }

  if (data.hasOwnProperty('minImageWidthEm')) {
    imageObject.minImageWidthEm = data.minImageWidthEm || 1.5;
  }

  if (data.hasOwnProperty('backgroundHexColor')) {
    const { valid, color } = hexToColor(data.backgroundHexColor);
    if (valid) {
      imageObject.backgroundColor = color;
    }
  }

  if (data.hasOwnProperty('fixedSize')) {
    imageObject.fixedSize = data.fixedSize;
  }

  if (!imageObject.maxImageWidthEm && !imageObject.maxImageHeightEm) {
    imageObject.maxImageWidthEm = 1.5;
    imageObject.maxImageHeightEm = 1.5;
  }

  if (!imageObject.id) {
    addGameObject(manager, imageObject);

    let appearDuration = 0.8;
    if (data.hasOwnProperty('appearImmediately')) {
      if (data.appearImmediately) {
        appearDuration = 0;
      }
    }
    initImageObject(
      manager,
      imageObject,
      data.label,
      data.imageUri,
      data.clientImageId,
      appearDuration,
    );

    setParentByEntityProps(manager, imageObject, data.sId, data.roomId, entityProps);
  } else {
    if (data.hasOwnProperty('imageUri')) {
      changeImageUri(imageObject, data.imageUri);
      //change image
    }
  }

  if (data.hasOwnProperty('noHover')) {
    imageObject.allowHighlight = !data.noHover;
    imageObject.imageEnt.allowHighlight = !data.noHover;
  }
}

export function initImageObject(
  manager: GameManager,
  imageObject: ImageObject,
  label: string,
  imageUri: string,
  clientImageId: string,
  appearDuration = 0.8,
) {
  imageObject.shape = Shape.RECT;
  imageObject.label = label;
  imageObject.imageUri = imageUri;
  imageObject.allowHighlight = true;
  imageObject.allowDrag = true;
  imageObject.clickable = true;
  imageObject.appearDuration = appearDuration;
  imageObject.disappearDuration = 0.4;

  const length = API_HOST.length;

  let imageUriSized = imageUri;
  if (clientImageId) {
    imageUriSized = clientImageId;
  } else if (imageUri.substring(0, length) === API_HOST) {
    if (imageObject.maxImageWidthEm < 2 && imageObject.maxImageHeightEm < 2) {
      imageUriSized = getImageUriBySize(imageUri, 'small');
    } else if (imageObject.maxImageWidthEm < 5 && imageObject.maxImageHeightEm < 5) {
      imageUriSized = getImageUriBySize(imageUri, 'medium');
    } else {
      imageUriSized = getImageUriBySize(imageUri, 'large');
    }

    if (DataController.getImageState(imageUriSized) === '') {
      DataController.cacheImageFromApiAsync(manager.user.accessToken, imageUriSized, imageUriSized);
    }
  } else {
    if (DataController.getImageState(imageUriSized) === '') {
      DataController.cacheImage(imageUri, imageUriSized);
    }
  }

  imageObject.imageEnt = createImageEnt(manager, getZeroStaticEntity(), imageUriSized, {
    appearOnLoad: true,
    appearStyle: appearDuration ? 'fade_and_scale' : 'none',
  });
  if (imageObject.fixedSize) {
    imageObject.imageEnt.disableAutoSize = true;
  }

  imageObject.imageEnt.appearDuration = appearDuration;
  imageObject.imageEnt.disappearDuration = 0.4;
  imageObject.imageEnt.maxWidthEm = imageObject.maxImageWidthEm;
  imageObject.imageEnt.maxHeightEm = imageObject.maxImageHeightEm;
  imageObject.imageEnt.allowDrag = true;
  imageObject.imageEnt.passDrag = true;
  imageObject.imageEnt.allowHighlight = true;
  imageObject.imageEnt.passHover = true;
  imageObject.imageEnt.clickable = true;
  imageObject.imageEnt.passClick = true;
  imageObject.imageEnt.passClickCanvas = true;

  if (label) {
    imageObject.textObject = createAndAddLocalTextObject(
      manager,
      getZeroStaticEntity(),
      label,
      TEXT_STYLE.IMAGE_LABEL,
      2,
      true,
    );
    imageObject.textObject.allowDrag = false;
    imageObject.textObject.passDrag = true;
    imageObject.textObject.allowHighlight = true;
    imageObject.textObject.passHover = true;
    imageObject.textObject.clickable = true;
    imageObject.textObject.passClick = true;
    imageObject.textObject.moveMode = MoveMode.ANCHORED;
    imageObject.textObject.anchoredPos = anchoredPos(
      v2(0.5, 1),
      v2(0, 0),
      v2(0.5, 0),
      imageObject.imageEnt.id,
    );
  }

  const tObjHeight = imageObject.textObject ? imageObject.textObject.height : 0;
  const imHeight =
    imageObject.imageEnt.height || imageObject.maxImageHeightEm * manager.rootUnitSizePx;
  setParent(imageObject.imageEnt, imageObject, v2());
  if (imageObject.textObject) {
    setParent(imageObject.textObject, imageObject, v2(0, imHeight / 2));
  }

  const border = 10;
  const totalHeight = imHeight + tObjHeight + border + border;
  imageObject.height = totalHeight;
  imageObject.width = imageObject.minImageWidthEm * manager.rootUnitSizePx + border + border;

  setVisibilityState(imageObject, VisibilityState.APPEARING);

  return imageObject;
}

function update(imageObj: ImageObject): boolean {
  const manager = imageObj.manager;

  if (imageObj.entityStateChanged) {
    setVisibilityState(imageObj.imageEnt, imageObj.visibilityState);
  }

  if (imageObj.imageEnt.layoutChanged) {
    if (imageObj.textObject) {
      const refEntity = manager.gameObjectIdTable[imageObj.textObject.anchoredPos.referenceId];
      const [x, y] = getEntityPositionXY(
        imageObj.textObject,
        imageObj.textObject.anchoredPos,
        refEntity,
      );
      setEntityPosition(imageObj.textObject, x, y);
    }

    imageObj.width = Math.max(
      imageObj.imageEnt.width + 20,
      imageObj.minImageWidthEm * manager.rootUnitSizePx,
    );

    if (imageObj.textObject) {
      //a bit hacky to calculate back...
      imageObj.textObject.maxWidthEm = (imageObj.width - 20) / manager.rootUnitSizePx;

      layoutTextObject(imageObj.textObject);
    }

    const textHeight = imageObj.textObject ? imageObj.textObject.height : 0;
    setEntityPosition(imageObj.imageEnt, 0, -textHeight / 2);
    imageObj.height = imageObj.imageEnt.height + textHeight + 20;
    imageObj.layoutChanged = true;
  }

  if (
    imageObj.previousImageEnt &&
    imageObj.imageEnt.visibilityState === VisibilityState.APPEARING
  ) {
    imageObj.previousImageEnt.disappearDuration = 0.4;
    setVisibilityState(imageObj.previousImageEnt, VisibilityState.DISAPPEARING);
    imageObj.previousImageEnt = null;
  }

  imageObj.scale = imageObj.imageEnt.scale;

  imageObj.width = Math.max(
    imageObj.imageEnt.width + 20,
    imageObj.minImageWidthEm * manager.rootUnitSizePx,
  );

  imageObj.height = imageObj.imageEnt.height + 20;

  return false;
}

export function createPackage(
  imageObject: ImageObject,
  position: V2,
  scale: number,
): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  if (imageObject.highlighted) {
    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x,
      positionY: position.y,
      width: imageObject.width + 20 * scale,
      height: imageObject.height + 20 * scale,
      shapeStyle: SHAPE_STYLES.ROOM_NODE_RING_1,
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
      shadow: null,
    };
    rPackage.push(rect);
  }

  {
    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x,
      positionY: position.y,
      width: imageObject.width,
      height: imageObject.height,
      shapeStyle: {
        ...SHAPE_STYLES.NONE,
        fillColor: multiplyColorAlpha255(
          imageObject.backgroundColor,
          imageObject.imageEnt.appearAlpha * 255,
        ),
      },
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
      shadow: null,
    };
    rPackage.push(rect);
  }

  return rPackage;
}

function changeImageUri(imageObject: ImageObject, imageUri: string) {
  if (imageUri === imageObject.imageUri) return;

  const manager = imageObject.manager;
  let imageUriSized = imageUri;

  const length = API_HOST.length;

  if (imageUri.substring(0, length) === API_HOST) {
    //TODO: add image dimensions to ImageSize function
    //based on that the correct image is downloaded
    if (imageObject.maxImageWidthEm < 2 && imageObject.maxImageHeightEm < 2) {
      imageUriSized = getImageUriBySize(imageUri, 'small');
    } else if (imageObject.maxImageWidthEm < 5 && imageObject.maxImageHeightEm < 5) {
      imageUriSized = getImageUriBySize(imageUri, 'medium');
    } else {
      imageUriSized = getImageUriBySize(imageUri, 'large');
    }

    if (DataController.getImageState(imageUriSized) === '') {
      DataController.cacheImageFromApiAsync(manager.user.accessToken, imageUriSized, imageUriSized);
    }
  }

  imageObject.previousImageEnt = imageObject.imageEnt;

  imageObject.imageEnt = createImageEnt(manager, getZeroStaticEntity(), imageUriSized, {
    appearOnLoad: true,
    appearStyle: 'fade',
  });
  if (imageObject.fixedSize) {
    imageObject.imageEnt.disableAutoSize = true;
  }

  if (imageObject.serverObjId) {
    imageObject.imageEnt.unloadImageOnDispose = true;
  }
  imageObject.imageEnt.appearDuration = 0.3;
  imageObject.imageEnt.disappearDuration = 0.4;
  imageObject.imageEnt.maxWidthEm = imageObject.maxImageWidthEm;
  imageObject.imageEnt.maxHeightEm = imageObject.maxImageHeightEm;
  imageObject.imageEnt.allowDrag = true;
  imageObject.imageEnt.passDrag = true;
  imageObject.imageEnt.allowHighlight = true;
  imageObject.imageEnt.passHover = true;
  imageObject.imageEnt.clickable = true;
  imageObject.imageEnt.passClick = true;
  imageObject.imageEnt.passClickCanvas = true;

  setParent(imageObject.imageEnt, imageObject, v2());
}
